import React, { useEffect, useState } from "react";
import {
  ShoppingBag,
  ArrowRight,
  ChevronLeft,
  CheckCircle2,
  Printer,
  Mail,
} from "lucide-react";
import {
  PACKAGES,
  TOWNS,
  lineBreakdown,
  cartPrice,
  extraChildren,
  linePrice,
  mapUrl,
  money,
} from "../shared/catalog.js";
import { Button, Field, ErrorMessage, TreePine } from "./components.jsx";
import { api } from "./api.js";
import { deadlineLabel as deadlineText } from "../shared/season.js";

export function Checkout({ cart, config, navigate, onEdit, onSuccess }) {
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [method, setMethod] = useState("card");
  const payment = config?.cardEnabled ? method : "cash";
  const [customer, setCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    unit: "",
    city: "Idaho Falls",
    zip: "",
    instructions: "",
    dietaryNotes: "",
  });
  const change = (key, value) => setCustomer((c) => ({ ...c, [key]: value }));
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    let checkoutKey = sessionStorage.getItem("whimsy-checkout-key");
    if (!checkoutKey) {
      checkoutKey = crypto.randomUUID();
      sessionStorage.setItem("whimsy-checkout-key", checkoutKey);
    }
    try {
      const { order, checkoutUrl, paymentError } = await api("/api/orders", {
        method: "POST",
        body: {
          checkoutKey,
          items: cart,
          customer,
          paymentMethod: payment,
          acceptedTerms: true,
        },
      });
      onSuccess({ ...order, checkoutKey, paymentError }, checkoutUrl);
    } catch (error) {
      setError(error.message);
    } finally {
      setBusy(false);
    }
  }
  if (!cart.length)
    return (
      <section className="section empty-state">
        <ShoppingBag size={45} />
        <h1>Your Christmas cart is empty.</h1>
        <Button onClick={() => navigate("/order")}>
          Choose a package <ArrowRight size={16} />
        </Button>
      </section>
    );
  return (
    <section className="section checkout-page">
      <button className="text-link" onClick={() => navigate("/order")}>
        <ChevronLeft size={16} /> Back to packages
      </button>
      <div className="checkout-heading">
        <span className="eyebrow">ONE LAST LITTLE STEP</span>
        <h1>Let’s make it magical.</h1>
        <p>
          Your surprise will be delivered December 6–12. Delivery is included.
        </p>
      </div>
      <form className="checkout-layout" onSubmit={submit}>
        <div className="checkout-fields">
          <section className="form-panel">
            <h2>
              <span>01</span> Your contact details
            </h2>
            <Field label="Your full name">
              <input
                required
                autoComplete="name"
                maxLength={100}
                value={customer.name}
                onChange={(e) => change("name", e.target.value)}
              />
            </Field>
            <div className="form-row">
              <Field label="Email address">
                <input
                  type="email"
                  required
                  autoComplete="email"
                  maxLength={254}
                  value={customer.email}
                  onChange={(e) => change("email", e.target.value)}
                />
              </Field>
              <Field
                label="Phone number"
                hint="Only for payment or delivery access questions."
              >
                <input
                  type="tel"
                  required
                  minLength={10}
                  maxLength={30}
                  autoComplete="tel"
                  value={customer.phone}
                  onChange={(e) => change("phone", e.target.value)}
                />
              </Field>
            </div>
          </section>
          <section className="form-panel">
            <h2>
              <span>02</span> Where should the elves stop?
            </h2>
            <Field label="Street address">
              <input
                required
                autoComplete="address-line1"
                maxLength={160}
                value={customer.address}
                onChange={(e) => change("address", e.target.value)}
              />
            </Field>
            <Field label="Apartment, unit or suite (optional)">
              <input
                autoComplete="address-line2"
                maxLength={60}
                value={customer.unit}
                onChange={(e) => change("unit", e.target.value)}
              />
            </Field>
            <div className="form-row">
              <Field label="Town">
                <select
                  required
                  value={customer.city}
                  onChange={(e) => change("city", e.target.value)}
                >
                  {TOWNS.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </Field>
              <Field label="Idaho ZIP code">
                <input
                  required
                  autoComplete="postal-code"
                  inputMode="numeric"
                  pattern="[0-9]{5}(-[0-9]{4})?"
                  maxLength={10}
                  value={customer.zip}
                  onChange={(e) => change("zip", e.target.value)}
                />
              </Field>
            </div>
            <Field label="Delivery instructions (optional)">
              <textarea
                maxLength={1000}
                value={customer.instructions}
                onChange={(e) => change("instructions", e.target.value)}
                placeholder="Gate code, sheltered spot, which door…"
                rows={3}
              />
            </Field>
            <Field
              label="Food preferences or substitution requests (optional)"
              hint="Requests need confirmation from the moms; substitutions aren’t guaranteed."
            >
              <textarea
                maxLength={600}
                value={customer.dietaryNotes}
                onChange={(e) => change("dietaryNotes", e.target.value)}
                rows={2}
              />
            </Field>
          </section>
          <section className="form-panel">
            <h2>
              <span>03</span> Payment
            </h2>
            {config?.cardEnabled && (
              <label className="payment-option">
                <input
                  type="radio"
                  name="payment"
                  value="card"
                  checked={payment === "card"}
                  onChange={() => setMethod("card")}
                />
                <span>
                  <strong>Pay online</strong>
                  <small>
                    Pay by card on Stripe’s secure checkout after you place your
                    order.
                  </small>
                </span>
                <span className="payment-tag">CARD</span>
              </label>
            )}
            <label className="payment-option">
              <input
                type="radio"
                name="payment"
                value="cash"
                checked={payment === "cash"}
                onChange={() => setMethod("cash")}
              />
              <span>
                <strong>Pay at drop-off</strong>
                <small>
                  Cash with your letters at a collection point by{" "}
                  {deadlineText(config)}.
                </small>
              </span>
              <span className="payment-tag">AVAILABLE</span>
            </label>
            {!config?.cardEnabled && (
              <div className="payment-option unavailable">
                <span className="radio-disabled" />
                <span>
                  <strong>Pay online</strong>
                  <small>Card payments are coming soon.</small>
                </span>
                <span className="payment-tag">COMING SOON</span>
              </div>
            )}
            {!config?.locations?.length && (
              <p className="inline-notice">
                Collection points are still being finalized. Please wait for
                locations to appear on the website before dropping off letters.
              </p>
            )}
          </section>
        </div>
        <aside className="checkout-review">
          <div className="review-card">
            <span className="eyebrow">YOUR CHRISTMAS SURPRISE</span>
            <h2>A little look inside.</h2>
            {cart.map((item, i) => {
              const p = PACKAGES.find((p) => p.id === item.packageId);
              const extra = extraChildren(item);
              return (
                <div className="review-line" key={i}>
                  <div>
                    <strong>{p.name}</strong>
                    <span>
                      {item.children
                        .map((c) => `${c.name} (${c.age})`)
                        .join(", ")}
                    </span>
                    <small>{lineBreakdown(item)}</small>
                    {p.kind !== "letter" && (
                      <small>
                        {item.children.length}{" "}
                        {item.children.length === 1 ? "child" : "children"}
                        {extra === 0 &&
                        item.children.length < p.includedChildren
                          ? ` · room for ${p.includedChildren - item.children.length} more at no extra cost`
                          : ` · includes up to ${p.includedChildren}`}
                      </small>
                    )}
                    <button type="button" onClick={() => onEdit(i)}>
                      Edit children
                    </button>
                  </div>
                  <strong>{money(linePrice(item))}</strong>
                </div>
              );
            })}
            <div className="cart-totals">
              <span>
                Children{" "}
                <strong>
                  {cart.reduce((n, item) => n + item.children.length, 0)}
                </strong>
              </span>
              <span>
                Doorstep delivery <strong>Included</strong>
              </span>
              <span className="cart-grand-total">
                Total <strong>{money(cartPrice(cart))}</strong>
              </span>
            </div>
            <label className="terms">
              <input type="checkbox" required />
              <span>
                I’ve checked my details. I understand payment is due by{" "}
                {deadlineText(config)}, delivery is December 6–12 with no
                appointment time, and all sales are final. Whimsy Delivered will
                try to help if something comes up, but refunds and changes
                aren’t guaranteed.
              </span>
            </label>
            <ErrorMessage>{error}</ErrorMessage>
            <Button
              className="full-width"
              type="submit"
              disabled={busy || !config?.acceptingOrders}
            >
              {busy
                ? "Saving your little magic…"
                : payment === "card"
                  ? "Place order & pay"
                  : "Place order"}
              <ArrowRight size={17} />
            </Button>
            <p className="fine-print">
              {config?.emailEnabled
                ? "We’ll email your order confirmation."
                : payment === "card"
                  ? "Stripe will email your payment receipt after successful payment. Please save your order number."
                  : "Please print or save your order receipt and order number."}
            </p>
          </div>
          <div className="review-note">
            <HeartNote />
          </div>
        </aside>
      </form>
    </section>
  );
}
function HeartNote() {
  return (
    <>
      <TreePine size={28} strokeWidth={1} />
      <p>
        Prepared by two local moms.
        <br />
        Made for your family’s memories.
      </p>
    </>
  );
}

export function Confirmation({ order, config, navigate, onPaid }) {
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("session_id"),
    canceled = params.get("payment") === "canceled";
  const [paymentNotice, setPaymentNotice] = useState(""),
    [paying, setPaying] = useState(false);
  useEffect(() => {
    if (!sessionId) return;
    setPaymentNotice("Checking your card payment…");
    api("/api/orders/payment?session_id=" + encodeURIComponent(sessionId))
      .then(({ number, paid }) => {
        if (paid && order?.number === number) onPaid();
        setPaymentNotice(
          paid
            ? ""
            : "Your card payment is still processing. We’ll email a receipt once it’s confirmed.",
        );
      })
      .catch(() =>
        setPaymentNotice(
          "We couldn’t confirm your card payment yet. We’ll email a receipt once it’s confirmed.",
        ),
      );
  }, [sessionId]);
  const paid = order && order.received >= order.total;
  async function payByCard() {
    setPaying(true);
    try {
      const { checkoutUrl } = await api("/api/orders/pay", {
        method: "POST",
        body: { checkoutKey: order.checkoutKey },
      });
      if (checkoutUrl) location.assign(checkoutUrl);
      else onPaid();
    } catch (error) {
      setPaymentNotice(error.message);
      setPaying(false);
    }
  }
  if (!order)
    return (
      <section className="section empty-state">
        <Mail size={45} />
        <h1>A little magic, on its way.</h1>
        <p>
          Find your order number in your confirmation email or the printout you
          saved.
        </p>
        <Button onClick={() => navigate("/")}>Back to home</Button>
      </section>
    );
  return (
    <section className="section confirmation-page">
      <CheckCircle2 size={46} strokeWidth={1.2} />
      <span className="eyebrow">YOUR FAMILY’S MAGIC IS RESERVED</span>
      <h1>
        Let the Christmas
        <br />
        <em>countdown begin.</em>
      </h1>
      <p>
        Thank you, {order.customer.name.split(" ")[0]}. We’ve saved your order.
      </p>
      <div className="confirmation-card">
        <span className="eyebrow">YOUR ORDER NUMBER</span>
        <strong className="order-number">{order.number}</strong>
        <h2 className="receipt-title">Order receipt</h2>
        <div className="receipt-items">
          {order.items.map((item, i) => (
            <div key={i}>
              <span>
                {PACKAGES.find((p) => p.id === item.packageId).name}
                <small>
                  {item.children.length}{" "}
                  {item.children.length === 1 ? "child" : "children"}
                </small>
              </span>
              <strong>{money(linePrice(item))}</strong>
            </div>
          ))}
        </div>
        <div className="confirmation-summary">
          <span>
            Total <strong>{money(order.total)}</strong>
          </span>
          <span>
            Payment{" "}
            <strong className="status-text">
              {paid ? "Paid — thank you!" : "Awaiting payment"}
            </strong>
          </span>
          <span>
            Delivery <strong>December 6–12</strong>
          </span>
        </div>
        {!paid && (canceled || order.paymentError || paymentNotice) && (
          <p className="inline-notice">
            {paymentNotice ||
              (canceled
                ? "Card payment wasn’t finished. Your order is saved—you can try again or pay at drop-off."
                : `${order.paymentError} Your order is saved—you can try again or pay at drop-off.`)}
          </p>
        )}
        {!paid &&
          config?.cardEnabled &&
          order.checkoutKey &&
          !sessionId &&
          order.paymentMethod === "card" && (
            <Button
              className="full-width"
              onClick={payByCard}
              disabled={paying}
            >
              {paying ? "Opening secure checkout…" : "Pay online by card"}
              <ArrowRight size={16} />
            </Button>
          )}
        <h2>Next stop: a letter collection point.</h2>
        <p>
          Write the address and order number below on the outside of each
          letter’s envelope, and drop your letters off by{" "}
          <strong>{deadlineText(config)}</strong>.
        </p>
        <div className="envelope-label">
          <small>WRITE THIS ON YOUR LETTER ENVELOPES</small>
          <strong>{order.customer.name}</strong>
          <span>
            {order.customer.address}
            {order.customer.unit && `, ${order.customer.unit}`}
            <br />
            {order.customer.city}, ID {order.customer.zip}
            <br />
            Order {order.number}
          </span>
        </div>
        {!paid && (
          <p className="fine-print">
            Paying at drop-off? Use a sealed envelope marked with your order
            number and name.
          </p>
        )}
        {config?.locations?.length ? (
          <>
            {config.locations.map((l) => (
              <p key={l.name}>
                <strong>{l.name}</strong>
                <br />
                <a href={mapUrl(l)} target="_blank" rel="noreferrer">
                  {l.address}
                </a>
                <br />
                {l.hours}
              </p>
            ))}
            <p className="fine-print">More locations coming soon.</p>
          </>
        ) : (
          <p className="inline-notice">
            Collection points are coming soon. Check the website before dropping
            off letters.
          </p>
        )}
        <p className="fine-print">
          {order.confirmationEmailStatus === "sent"
            ? `Confirmation sent to ${order.customer.email}. We’ll send a receipt when your payment is confirmed, and another email after delivery.`
            : config?.emailEnabled
              ? "Your order is saved. Email delivery is delayed; please print or save this order number."
              : order.paymentMethod === "card"
                ? "Stripe will email your payment receipt after successful payment. Please print or save this order number."
                : "Please print or save your order receipt and order number."}
        </p>
        <Button
          className="button-outline full-width"
          onClick={() => window.print()}
        >
          <Printer size={16} /> Print receipt
        </Button>
      </div>
      <button className="text-link" onClick={() => navigate("/")}>
        Back to our little Christmas corner <ArrowRight size={16} />
      </button>
    </section>
  );
}
